import React, { useEffect, useState, useRef } from "react";
import CommonTable from "../../components/commonComponent/CommanTable";
import axios from '../../utils/axiosInstance';
import { FaPencilAlt, FaTrashAlt, FaEye, FaUserCheck, FaMapMarkerAlt, FaCalendarCheck, FaEllipsisV, FaUpload, FaFilter, FaDownload, FaPlus, FaEye as FaEyeIcon } from 'react-icons/fa';
import { useNavigate } from "react-router-dom";
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { toastSuccess, toastError, confirmDelete } from "../../utils/alertHelper";

// Permission helpers
const getLeadPermissions = (permissionGroups) => {
  if (!Array.isArray(permissionGroups)) return {};
  const leadGroup = permissionGroups.find(g =>
    g.group?.name?.toLowerCase() === "lead"
  );
  if (!leadGroup) return {};
  const leadPerm = leadGroup.permissions.find(
    p => p.permission?.name?.toLowerCase() === "lead"
  );
  if (!leadPerm) return {};
  const allowed = leadPerm.allowedActions.map(a => a.name?.toLowerCase());
  // Normalize names for easier checks
  return {
    canList: allowed.includes("list"),
    canView: allowed.includes("view"),
    canCreate: allowed.includes("create"),
    canEdit: allowed.includes("edit"),
    canDelete: allowed.includes("delete"),
    canFollowup: allowed.includes("followup"),
    canConvert: allowed.includes("convertcustomers"),
    canExport: allowed.includes("export"),
    canImport: allowed.includes("import"),
    canVisit: allowed.includes("visit"),
  };
};

const allColumns = [
  { key: "srNo", label: "Sr.no", width: 60 },
  { key: "leadNumber", label: "Lead Number" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Mobile Number" },
  { key: "gstNumber", label: "GST Number" },
  { key: "address", label: "Address" },
  { key: "area", label: "Area" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "country", label: "Country" },
  { key: "pinCode", label: "Pin Code" },
  { key: "birthDate", label: "Birth Date" },
  { key: "anniversaryDate", label: "Anniversary Date" },
  { key: "projectType", label: "Project Type" },
  { key: "leadSource", label: "Lead Source" },
  { key: "leadCreatedBy", label: "Lead Created By" },
  { key: "leadOwner", label: "Lead Owner" },
  { key: "dealStage", label: "Deal Stage" },
  { key: "dealType", label: "Deal Type" },
  { key: "dealTemperature", label: "Deal Temperature" },
  { key: "referralName", label: "Referral Name" },
  { key: "referralNumber", label: "Referral Number" },
  { key: "lostReason", label: "Lost Reason" },
  { key: "notes", label: "Notes" },
  { key: "date", label: "Date" },
  { key: "action", label: "Action", type: "actions", width: 120 },
];

const defaultFilter = {
  search: "",
  dealStage: "",
  dealType: "",
  leadOwner: "",
  city: "",
  state: "",
  country: "",
  startDate: "",
  endDate: "",
};

const LeadIndex = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [filter, setFilter] = useState(defaultFilter);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [visitLead, setVisitLead] = useState(null);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [followupLead, setFollowupLead] = useState(null);
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef(null);

  const forcedFields = ['srNo', 'action'];
  const getInitialVisibleFields = () => {
    const stored = localStorage.getItem('lead_visible_columns');
    return stored ? JSON.parse(stored) : allColumns.map(f => f.key);
  };
  const [visibleFields, setVisibleFields] = useState(getInitialVisibleFields);

  useEffect(() => {
    localStorage.setItem('lead_visible_columns', JSON.stringify(visibleFields));
  }, [visibleFields]);

  useEffect(() => {
    if (!showColumnDropdown) return;
    function handleClickOutside(event) {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target)) {
        setShowColumnDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnDropdown]);

  const [visitFormData, setVisitFormData] = useState({
    name: "",
    visitAt: "",
    note: "",
    user: "",
    lead: "",
  });
  const [followupFormData, setFollowupFormData] = useState({
    date: "",
    note: ""
  });
  const [userOptions, setUserOptions] = useState([]);
  const [dealStageOptions, setDealStageOptions] = useState([]);
  const [dealTypeOptions, setDealTypeOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [leadOwnerOptions, setLeadOwnerOptions] = useState([]);
  const actionMenuRef = useRef();
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const [businessId, setBusinessId] = useState("");

  // Permissions state
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [leadPermissions, setLeadPermissions] = useState({});

  // Example: Get permissions from localStorage, replace with your auth persistence
  useEffect(() => {
    try {
      const saved = localStorage.getItem("permissionGroups");
      if (saved) {
        const parsed = JSON.parse(saved);
        setPermissionGroups(parsed);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    setLeadPermissions(getLeadPermissions(permissionGroups));
  }, [permissionGroups]);

  useEffect(() => {
    const storedBusinessId = localStorage.getItem("businessId");
    if (storedBusinessId) {
      setBusinessId(storedBusinessId);
    } else {
      console.warn("Business ID not found in localStorage");
    }
  }, []);

  // Fetch table data
  useEffect(() => {
    if (!leadPermissions.canList && !leadPermissions.canView) return;
    const fetchLeads = async () => {
      if (!businessId) return;
      setLoading(true);
      try {
        const params = {
          business: businessId,
          page,
          limit: perPage,
          search: filter.search,
          dealStage: filter.dealStage,
          dealType: filter.dealType,
          leadOwner: filter.leadOwner,
          city: filter.city,
          state: filter.state,
          country: filter.country,
          startDate: filter.startDate,
          endDate: filter.endDate,
        };
        const res = await axios.get("/admin/api/lead", { params });
        setLeads(res.data.leads || []);
        setTotal(res.data.totalCount || 0);
      } catch (e) {
        console.error("Error fetching leads:", e);
        setLeads([]);
      }
      setLoading(false);
    };
    fetchLeads();
  }, [filter, page, perPage, businessId, leadPermissions]);

  useEffect(() => {
    async function fetchUsers() {
      if (!businessId) return;
      try {
        const userType = localStorage.getItem("userType") || "Company";
        const params = new URLSearchParams();
        params.append('userType', userType);
        params.append('business', businessId);
        const res = await axios.get(`/api/user?${params.toString()}`);
        if (res.data && Array.isArray(res.data.users)) {
          setUserOptions(
            res.data.users.map(u => ({
              value: u._id,
              label: `${u.firstName || ""} ${u.lastName || ""}`.trim()
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching users:", err);
        setUserOptions([]);
      }
    }
    fetchUsers();
  }, [businessId]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const dealStageRes = await axios.get(`/admin/api/deal-stage?business=${businessId}`);
        if (dealStageRes.data && Array.isArray(dealStageRes.data.dealStages)) {
          setDealStageOptions(
            dealStageRes.data.dealStages.map(stage => ({
              value: stage._id,
              label: stage.name
            }))
          );
        }
        const dealTypeRes = await axios.get(`/admin/api/deal-type?business=${businessId}`);
        if (dealTypeRes.data && Array.isArray(dealTypeRes.data.dealTypes)) {
          setDealTypeOptions(
            dealTypeRes.data.dealTypes.map(type => ({
              value: type._id,
              label: type.name
            }))
          );
        }
        const cityRes = await axios.get('/admin/api/city');
        if (cityRes.data && Array.isArray(cityRes.data.cities)) {
          setCityOptions(
            cityRes.data.cities.map(city => ({
              value: city._id,
              label: city.name
            }))
          );
        }
        const stateRes = await axios.get('/admin/api/state');
        if (stateRes.data && Array.isArray(stateRes.data.states)) {
          setStateOptions(
            stateRes.data.states.map(state => ({
              value: state._id,
              label: state.name
            }))
          );
        }
        const countryRes = await axios.get('/admin/api/country');
        if (countryRes.data && Array.isArray(countryRes.data.countries)) {
          setCountryOptions(
            countryRes.data.countries.map(country => ({
              value: country._id,
              label: country.name
            }))
          );
        }
        const userType = localStorage.getItem("userType") || "Company";
        const userParams = new URLSearchParams();
        userParams.append('userType', userType);
        userParams.append('business', businessId);
        const userRes = await axios.get(`/api/user?${userParams.toString()}`);
        if (userRes.data && Array.isArray(userRes.data.users)) {
          setLeadOwnerOptions(
            userRes.data.users.map(user => ({
              value: user._id,
              label: `${user.firstName || ""} ${user.lastName || ""}`.trim()
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        setDealStageOptions([]);
        setDealTypeOptions([]);
        setCityOptions([]);
        setStateOptions([]);
        setCountryOptions([]);
        setLeadOwnerOptions([]);
      }
    };
    if (businessId) {
      fetchDropdownData();
    }
  }, [businessId]);

  // DEMO SHEET DOWNLOAD
  const handleDemoSheetDownload = async () => {
    if (!leadPermissions.canImport) {
      toastError("You do not have permission to download the sample sheet.");
      return;
    }
    try {
      const response = await axios.get('/admin/api/lead/export-sample', { responseType: 'blob' });
      const filename = 'demoLeadSheet.xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toastSuccess("Demo sheet downloaded!");
    } catch (err) {
      toastError("Failed to download demo sheet.");
    }
  };

  //lead failed data
  const downloadFailedDataFile = (blobData, filename = "failed_leads.xlsx") => {
    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // LEAD EXCEL IMPORT
  const handleLeadExcelImport = async () => {
    if (!leadPermissions.canImport) {
      toastError("You do not have permission to import leads.");
      return;
    }
    if (!uploadFile) {
      toastError("Please select a file to upload.");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      const response = await axios.post("/admin/api/lead/import-excel", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: 'blob'
      });

      if (response.data && typeof response.data === 'object' && response.data.Status === 1) {
        toastSuccess("Leads imported successfully!");
        setShowUploadModal(false);
        setUploadFile(null);
        setFilter(prev => ({ ...prev }));
      } else if (response.headers['content-type']?.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') || response.headers['content-type']?.includes('application/octet-stream')) {
        downloadFailedDataFile(response.data, "failed_leads.xlsx");
        toastError("Some leads failed to import. Failed data downloaded.");
      } else {
        let errorMsg = "Failed to import leads.";
        try {
          const text = await response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.Message || errorMsg;
        } catch {}
        toastError(errorMsg);
      }
    } catch (err) {
      if (err.response && err.response.data) {
        const contentType = err.response.headers['content-type'];
        if (contentType?.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') || contentType?.includes('application/octet-stream')) {
          downloadFailedDataFile(err.response.data, "failed_leads.xlsx");
          toastError("Some leads failed to import. Failed data downloaded.");
          return;
        }
        let msg = err?.response?.data?.Message || err?.message || "Import failed.";
        if (err.response.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const json = JSON.parse(text);
            msg = json.Message || msg;
          } catch {}
        }
        toastError(msg);
      } else {
        toastError(err?.message || "Import failed.");
      }
      console.error("Import failed:", err);
    }
  };

  const handleEdit = (lead) => {
    if (!leadPermissions.canEdit) { toastError("No permission to edit."); return; }
    navigate(`/user/lead/edit/${lead._id}`);
  };

  const handleDelete = async (lead) => {
    if (!leadPermissions.canDelete) { toastError("No permission to delete."); return; }
    try {
      const res = await confirmDelete("Delete this lead?");
      if (!res.isConfirmed) return;
    } catch (err) {
      return;
    }
    try {
      await axios.delete(`/admin/api/lead/${lead._id}`);
      setLeads(leads.filter((l) => l._id !== lead._id));
      toastSuccess("Lead deleted successfully!");
    } catch (error) {
      console.error("Error deleting lead:", error);
      toastError("Failed to delete lead. Please try again.");
    }
  };

  const handleBulkDelete = async () => {
    if (!leadPermissions.canDelete) { toastError("No permission to delete."); return; }
    try {
      const res = await confirmDelete("Delete selected leads?");
      if (!res.isConfirmed) return;
    } catch (err) {
      return;
    }
    try {
      await axios.post("/admin/api/lead/bulk-delete", { ids: selectedRows });
      setLeads(leads.filter((l) => !selectedRows.includes(l._id)));
      setSelectedRows([]);
      toastSuccess(`${selectedRows.length} leads deleted successfully!`);
    } catch (error) {
      console.error("Error deleting leads:", error);
      toastError("Failed to delete selected leads. Please try again.");
    }
  };

  const handleExport = async () => {
    if (!leadPermissions.canExport) {
      toastError("You do not have permission to export leads.");
      return;
    }
    if (!businessId) {
      toastError("Business ID not found. Please login again.");
      return;
    }
    try {
      setLoading(true);
      const params = {
        business: businessId,
        search: filter.search,
        dealStage: filter.dealStage,
        dealType: filter.dealType,
        leadOwner: filter.leadOwner,
        city: filter.city,
        state: filter.state,
        country: filter.country,
        startDate: filter.startDate,
        endDate: filter.endDate,
      };
      Object.keys(params).forEach(key => {
        if (params[key] === "" || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      const response = await axios.get("/admin/api/lead/export-excel", {
        params,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'leads_export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toastSuccess("Export completed successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toastError("Export failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = () => {
    if (!leadPermissions.canImport) { toastError("No permission to upload/import."); return;}
    setShowUploadModal(true);
  };
  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
  };
  const handleLeadVisit = (lead) => {
    if (!leadPermissions.canVisit) { toastError("No permission for Lead Visit."); return;}
    setVisitLead(lead);
    setVisitFormData({
      visitAt: "",
      note: "",
      user: "",
      lead: lead._id,
    });
    setShowVisitModal(true);
  };
  const handleCloseVisitModal = () => {
    setShowVisitModal(false);
    setVisitLead(null);
  };
  const handleNextFollowup = (lead) => {
    if (!leadPermissions.canFollowup) { toastError("No permission for followup."); return;}
    setFollowupLead(lead);
    setFollowupFormData({
      date: "",
      note: "",
    });
    setShowFollowupModal(true);
  };
  const handleCloseFollowupModal = () => {
    setShowFollowupModal(false);
    setFollowupLead(null);
    setFollowupFormData({
      date: "",
      note: ""
    });
  };
  const handleSelectRow = (id, checked) => {
    setSelectedRows((prev) =>
      checked ? [...prev, id] : prev.filter((rowId) => rowId !== id)
    );
  };
  const handleSelectAll = (checked) => {
    setSelectedRows(checked ? leads.map((l) => l._id) : []);
  };
  const handleFilterChange = (newFilter) => {
    setFilter({ ...filter, ...newFilter });
    setPage(1);
  };
  const handleView = (lead) => {
    if (!leadPermissions.canView) { toastError("No permission to view."); return;}
    navigate(`/user/lead/view/${lead._id}`);
  };
  const handleConvert = (lead) => {
    if (!leadPermissions.canConvert) { toastError("No permission to convert."); return;}
    toastSuccess(`Convert to Customer: ${lead.name}`);
  };
  const handleVisitSubmit = async () => {
    if (!leadPermissions.canVisit) { toastError("No permission for Lead Visit."); return;}
    try {
      const payload = {
        visitAt: visitFormData.visitAt,
        note: visitFormData.note,
        user: visitFormData.user,
        lead: visitFormData.lead,
        business: businessId,
      };
      const res = await axios.post("/admin/api/lead-visit", payload);
      if (res.data && res.data.Status === 1) {
        toastSuccess("Lead Visit added successfully!");
        setShowVisitModal(false);
        setVisitLead(null);
        setVisitFormData({
          visitAt: "",
          note: "",
          user: "",
          lead: "",
        });
      } else {
        toastError(res.data.Message || "Failed to add lead visit");
      }
    } catch (err) {
      console.error("Error adding lead visit:", err);
      toastError("Failed to add lead visit. Please try again.");
    }
  };
  const handleFollowupSubmit = async () => {
    if (!leadPermissions.canFollowup) { toastError("No permission for Followup."); return;}
    try {
      const payload = {
        note: followupFormData.note,
        lead: followupLead?._id,
        nextdate: followupFormData.date,
        business: businessId,
      };
      const res = await axios.post("/admin/api/lead-followup", payload);
      if (res.data && res.data.Status === 1) {
        toastSuccess("Followup added successfully!");
        setShowFollowupModal(false);
        setFollowupLead(null);
        setFollowupFormData({
          date: "",
          note: ""
        });
      } else {
        toastError(res.data.Message || "Failed to add followup");
      }
    } catch (err) {
      console.error("Error adding followup:", err);
      toastError("Failed to add followup. Please try again.");
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (event.target && event.target.closest && event.target.closest('[data-action-menu-button]')) return;
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActionMenuOpen(null);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Filter columns based on visibility settings
  const visibleColumns = allColumns.filter(col =>
    forcedFields.includes(col.key) || visibleFields.includes(col.key)
  );

  const tableData = leads.map((lead, idx) => ({
    select: (
      <input
        type="checkbox"
        checked={selectedRows.includes(lead._id)}
        onChange={e => handleSelectRow(lead._id, e.target.checked)}
      />
    ),
    srNo: (page - 1) * perPage + idx + 1,
    leadNumber: lead.leadNumber || "",
    name: lead.name || "",
    email: lead.email || "",
    mobile: lead.mobile || "",
    gstNumber: lead.gst_number || "",
    address: lead.address || "",
    area: lead.area || "",
    city: typeof lead.city === 'object' ? lead.city?.name : lead.city || "",
    state: typeof lead.state === 'object' ? lead.state?.name : lead.state || "",
    country: typeof lead.country === 'object' ? lead.country?.name : lead.country || "",
    pinCode: lead.pin_code || "",
    birthDate: lead.bithDate ? new Date(lead.bithDate).toLocaleDateString('en-GB') : "",
    anniversaryDate: lead.anniversaryDate ? new Date(lead.anniversaryDate).toLocaleDateString('en-GB') : "",
    projectType: typeof lead.projectType === 'object' ? lead.projectType?.name : lead.projectType || "",
    leadSource: typeof lead.leadSource === 'object' ? lead.leadSource?.name : lead.leadSource || "",
    leadCreatedBy: lead.createdBy?.firstName && lead.createdBy?.lastName
      ? `${lead.createdBy.firstName} ${lead.createdBy.lastName}`
      : lead.createdBy?.firstName || lead.createdBy?.lastName || "",
    leadOwner: lead.leadOwner?.firstName && lead.leadOwner?.lastName
      ? `${lead.leadOwner.firstName} ${lead.leadOwner.lastName}`
      : lead.leadOwner?.firstName || lead.leadOwner?.lastName || "",
    dealStage: typeof lead.dealStage === 'object' ? lead.dealStage?.name : lead.dealStage || "",
    dealType: typeof lead.dealType === 'object' ? lead.dealType?.name : lead.dealType || "",
    dealTemperature: typeof lead.dealTemprature === 'object' ? lead.dealTemprature?.name : lead.dealTemprature || "",
    referralName: lead.ReferralName || "",
    referralNumber: lead.ReferralNumber || "",
    lostReason: lead.lostReason || "",
    notes: lead.notes || "",
    date: lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('en-GB') : "",
    action: (
      <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
        {leadPermissions.canVisit &&
        <button
          className="btn btn-sm me-1 py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
          title="Lead Visit"
          onClick={() => handleLeadVisit(lead)}
        >
          <FaMapMarkerAlt size={14} />
        </button>
        }
        {leadPermissions.canFollowup &&
        <button
          className="btn btn-sm me-1 py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
          title="Next Followup"
          onClick={() => handleNextFollowup(lead)}
        >
          <FaCalendarCheck size={14} />
        </button>
        }
        <button
          className="btn btn-sm me-1 py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
          style={{ cursor: "pointer" }}
          data-action-menu-button
          aria-haspopup="true"
          aria-expanded={actionMenuOpen === lead._id}
          onClick={(e) => {
            e.stopPropagation();
            const btn = e.currentTarget;
            const rect = btn.getBoundingClientRect();
            setActionMenuAnchor({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
            setActionMenuOpen(lead._id);
          }}
        >
          <FaEllipsisV size={14} />
        </button>
        {actionMenuOpen === lead._id && (
          <div
            ref={actionMenuRef}
            style={{
              position: "fixed",
              zIndex: 1050,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 6,
              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
              minWidth: 140,
              maxWidth: 260,
              padding: "6px 0",
              top: actionMenuAnchor?.top || undefined,
              left: actionMenuAnchor?.left || undefined
            }}
          >
            {leadPermissions.canView &&
            <button className="btn btn-sm w-100 text-start py-1 px-2 lh-sm shadow-none border-0 text-primary-custom" onClick={() => handleView(lead)}>
              <FaEye size={14} className="me-2" /> View
            </button>
            }
            {leadPermissions.canEdit && 
            <button className="btn btn-sm w-100 text-start py-1 px-2 lh-sm shadow-none border-0 text-primary-custom" onClick={() => handleEdit(lead)}>
              <FaPencilAlt size={14} className="me-2" /> Edit
            </button>
            }
            {leadPermissions.canDelete &&
            <button className="btn btn-sm w-100 text-start py-1 px-2 lh-sm shadow-none border-0 text-danger" onClick={() => handleDelete(lead)}>
              <FaTrashAlt size={14} className="me-2" /> Delete
            </button>
            }
          </div>
        )}
      </div>
    ),
  }));

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
        <h2 className="fw-bold">Leads</h2>
        <div className="d-flex gap-2 align-items-center">
         {leadPermissions.canImport &&
         <button className="btn btn-light" onClick={handleDemoSheetDownload}>
          <FaUpload className="me-1" /> Download Demo Sheet
        </button>
         }
         {leadPermissions.canImport &&
          <button className="btn btn-light" onClick={handleUpload}>
            <FaUpload className="me-1" /> Upload Lead
          </button>
         }
          <button
            className={`btn btn-light ${showFilter ? "active" : ""}`}
            onClick={() => setShowFilter((prev) => !prev)}
          >
            <FaFilter className="me-1" /> Filter
          </button>
         {leadPermissions.canExport &&
          <button className="btn btn-light" onClick={handleExport} disabled={loading}>
            <FaDownload className="me-1" /> {loading ? "Exporting..." : "Export"}
          </button>
         }
         {leadPermissions.canCreate &&
          <a href="/user/lead/create" className="btn btn-primary">
            <FaPlus className="me-1" /> Add New Lead
          </a>
         }
          <div style={{ position: 'relative' }} ref={columnDropdownRef}>
            <button
              className="btn btn-sm btn-light"
              onClick={() => setShowColumnDropdown(show => !show)}
              title="Column Visibility"
            >
              <FaEyeIcon className="me-1" />
            </button>
            {showColumnDropdown && (
              <div
                className="dropdown-menu show p-3"
                style={{
                  maxHeight: 250,
                  overflowY: 'auto',
                  right: 0,
                  left: 'auto',
                  position: 'absolute',
                  zIndex: 1050,
                  width: 'max-content',
                  minWidth: 220,
                }}
              >
                {allColumns
                  .filter(col => col.key !== 'action' && col.key !== 'select')
                  .map(col => (
                    <div key={col.key} className="form-check mb-1">
                      <input
                        type="checkbox"
                        id={`col-filter-${col.key}`}
                        className="form-check-input"
                        checked={visibleFields.includes(col.key) || forcedFields.includes(col.key)}
                        disabled={forcedFields.includes(col.key)}
                        onChange={e => {
                          if (e.target.checked) {
                            setVisibleFields(prev => {
                              const allKeys = allColumns.map(c => c.key);
                              const newCols = [...prev, col.key];
                              const uniqueCols = Array.from(new Set(newCols));
                              uniqueCols.sort((a, b) => allKeys.indexOf(a) - allKeys.indexOf(b));
                              return uniqueCols;
                            });
                          } else {
                            setVisibleFields(prev => prev.filter(k => k !== col.key));
                          }
                        }}
                      />
                      <label htmlFor={`col-filter-${col.key}`} className="form-check-label">
                        {col.label}
                      </label>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-end mb-3" style={{ maxWidth: "100%", gap: "10px" }}>
        <div className="input-group" style={{ maxWidth: 280, borderRadius: "6px", overflow: "hidden" }}>
          <span className="input-group-text bg-white border-end-0" style={{ borderRadius: "6px 0 0 6px" }}>
            <i className="fas fa-search text-muted"></i>
          </span>
          <input
            type="search"
            className="form-control border-start-0"
            placeholder="Type to search..."
            value={filter.search}
            onChange={(e) => setFilter((prev) => ({ ...prev, search: e.target.value }))}
            style={{ boxShadow: "none", borderRadius: "0 6px 6px 0" }}
          />
        </div>
      </div>
      {showFilter && (
        <div className="mb-4">
          <div className="card p-4">
            <h6 className="mb-3 fw-bold">Filter by</h6>
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label text-muted">Select Date</label>
                <div className="position-relative">
                  <input
                    type="date"
                    className="form-control"
                    value={filter.startDate}
                    onChange={(e) => setFilter(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label text-muted">Deal Stage</label>
                <select
                  className="form-select"
                  value={filter.dealStage}
                  onChange={(e) => setFilter(prev => ({ ...prev, dealStage: e.target.value }))}
                >
                  <option value="">Select Deal Stage</option>
                  {dealStageOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label text-muted">Deal Type</label>
                <select
                  className="form-select"
                  value={filter.dealType}
                  onChange={(e) => setFilter(prev => ({ ...prev, dealType: e.target.value }))}
                >
                  <option value="">Select Deal Types</option>
                  {dealTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label text-muted">Lead Owner</label>
                <select
                  className="form-select"
                  value={filter.leadOwner}
                  onChange={(e) => setFilter(prev => ({ ...prev, leadOwner: e.target.value }))}
                >
                  <option value="">Select Lead Owner</option>
                  {leadOwnerOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label text-muted">City</label>
                <select
                  className="form-select"
                  value={filter.city}
                  onChange={(e) => setFilter(prev => ({ ...prev, city: e.target.value }))}
                >
                  <option value="">Select City</option>
                  {cityOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label text-muted">State</label>
                <select
                  className="form-select"
                  value={filter.state}
                  onChange={(e) => setFilter(prev => ({ ...prev, state: e.target.value }))}
                >
                  <option value="">Select State</option>
                  {stateOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label text-muted">Country</label>
                <select
                  className="form-select"
                  value={filter.country}
                  onChange={(e) => setFilter(prev => ({ ...prev, country: e.target.value }))}
                >
                  <option value="">Select Country</option>
                  {countryOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-12 d-flex justify-content-center gap-3 mt-4">
                <button className="btn btn-primary px-4" style={{ borderRadius: '8px' }}>
                  Apply Filter
                </button>
                <button
                  className="btn btn-secondary px-4"
                  onClick={() => setFilter(defaultFilter)}
                  style={{ borderRadius: '8px' }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : leads.length > 0 ? (
        <CommonTable
          columns={visibleColumns}
          data={tableData}
          itemsPerPageOptions={[10, 15, 25, 50]}
          defaultItemsPerPage={10}
          totalCount={total}
          currentPage={page}
          onPageChange={setPage}
        />
      ) : (
        <div className="text-center py-5">
          <h5 className="text-muted">No leads found</h5>
          <p className="text-muted">
            {filter.search ? "Try adjusting your search criteria" : "Get started by adding your first lead"}
          </p>
        </div>
      )}
      {showUploadModal && (
        <CommonModalForm
          visible={showUploadModal}
          title="Upload lead"
          onCancel={handleCloseUploadModal}
          type="file"
          values={{ excel: uploadFile }}
          onChange={(newValues) => {
            setUploadFile(newValues.excel || null);
          }}
          fields={[
            {
              name: "excel",
              label: "Upload Excel",
              type: "file",
              accept: ".xlsx,.xls",
              placeholder: "Upload Excel",
            }
          ]}
          onSubmit={handleLeadExcelImport}
          submitLabel={uploadFile ? 'Upload' : 'Upload'}
        />
      )}
      {showVisitModal && (
        <CommonModalForm
          visible={showVisitModal}
          title="Leads Visit"
          onCancel={handleCloseVisitModal}
          type="visit"
          lead={visitLead}
          onSubmit={handleVisitSubmit}
          values={visitFormData}
          onChange={setVisitFormData}
          fields={[
            {
              name: "visitAt",
              label: "Select Date",
              type: "date",
              placeholder: "Select Date",
              min: new Date().toISOString().split('T')[0]
            },
            {
              name: "user",
              label: "Select User",
              type: "select",
              placeholder: "Select User",
              options: [{ value: "", label: "Select User" }, ...userOptions]
            },
            {
              name: "note",
              label: "Note",
              type: "textarea",
              placeholder: "Enter note",
            }
          ]}
        />
      )}
      {showFollowupModal && (
        <CommonModalForm
          visible={showFollowupModal}
          title="Next Follow-Up"
          onCancel={handleCloseFollowupModal}
          type="followup"
          lead={followupLead}
          onSubmit={handleFollowupSubmit}
          values={followupFormData}
          onChange={setFollowupFormData}
          fields={[
            {
              name: "date",
              label: "Select Date",
              type: "date",
              placeholder: "Select Date",
              min: new Date().toISOString().split('T')[0]
            },
            {
              name: "note",
              label: "Note",
              type: "textarea",
              placeholder: "Enter note"
            }
          ]}
        />
      )}
    </div>
  );
};

export default LeadIndex;